import {
  RadioGroup,
  RadioButton,
  Text,
} from '@cubeyou-team/hx-react-components';
import React, { FC, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import translations from 'src/lang';
import { interpolate } from '@discover-frontend/utils';
import { getTargets } from 'src/selectors/queryBuilder';
import { isEmpty } from 'src/utils/misc';

const { STANLEY_QUERY: translation } = translations['en-US'];
const qbTranslation = translation.MODAL.USE_QUERY_MODAL;

export const USE_QUERY_MODES = {
  CREATE_NEW_AUDIENCE: 'create-new-audience',
  ADD_TO_AUDIENCE: 'add-to-audience',
  OVERWRITE_EXISTING_AUDIENCE: 'overwrite-existing-audience',
} as const;

export type UseQueryMode =
  (typeof USE_QUERY_MODES)[keyof typeof USE_QUERY_MODES];

export interface StanleyUseQueryModeSelectorProps {
  promptText: string;
  createNewLabel: string;
  name?: string;
  selectedValue: UseQueryMode;
  onChange?: (value: UseQueryMode) => void;
}

export const StanleyUseQueryModeSelector: FC<
  StanleyUseQueryModeSelectorProps
> = ({
  promptText,
  createNewLabel,
  name = 'stanley-use-query-as',
  selectedValue,
  onChange,
}) => {
  const targets = useSelector(getTargets);
  const currentAudienceName = useSelector(
    (state: {
      queryBuilder: {
        targets: Record<string, { target_label?: string }>;
        target_selected: string;
      };
    }) => {
      const { targets, target_selected: selectedId } = state.queryBuilder;
      const target = targets[selectedId];

      if (!target) return '';

      if (!isEmpty(target.target_label)) return target.target_label;

      const tabIndex = Object.keys(targets).indexOf(selectedId);
      return `Target audience ${tabIndex >= 0 ? tabIndex + 1 : 1}`;
    }
  );

  const displayCreateNewAudience = useMemo(() => targets.length < 3, [targets]);

  const addToLabel = useMemo(
    () =>
      interpolate(qbTranslation.ADD_TO_LABEL, {
        name: currentAudienceName,
      }) ?? '',
    [currentAudienceName]
  );

  const overwriteLabel = useMemo(
    () =>
      interpolate(qbTranslation.OVERWRITE_LABEL, {
        name: currentAudienceName,
      }) ?? '',
    [currentAudienceName]
  );

  const handleOnChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(e.target.value as UseQueryMode);
    },
    [onChange]
  );

  return (
    <div className="flex flex-col gap-2">
      <Text size="x_body" className="pb-4">
        {promptText}
      </Text>
      <RadioGroup
        selected={selectedValue}
        name={name}
        onChange={handleOnChange}
      >
        {displayCreateNewAudience && (
          <RadioButton value={USE_QUERY_MODES.CREATE_NEW_AUDIENCE}>
            <Text>{createNewLabel}</Text>
          </RadioButton>
        )}
        <RadioButton value={USE_QUERY_MODES.ADD_TO_AUDIENCE}>
          <Text>{addToLabel}</Text>
        </RadioButton>
        <RadioButton value={USE_QUERY_MODES.OVERWRITE_EXISTING_AUDIENCE}>
          <Text>{overwriteLabel}</Text>
        </RadioButton>
      </RadioGroup>
    </div>
  );
};
